/**
 * Blog Post Metadata
 *
 * Displays breadcrumb, author info, date, likes, and share buttons
 */
'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Heart, Link2 } from 'lucide-react';
import { Post } from '@/types/post';

interface BlogPostMetaProps {
  post: Post;
  category?: string;
}

export default function BlogPostMeta({ post, category = 'AI & Automate' }: BlogPostMetaProps) {
  const [likes, setLikes] = useState(4600);
  const [isLiked, setIsLiked] = useState(false);

  const handleLike = () => {
    if (isLiked) {
      setLikes(likes - 1);
    } else {
      setLikes(likes + 1);
    }
    setIsLiked(!isLiked);
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = (platform: string) => {
    const urls = {
      x: `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      link: shareUrl,
    };

    if (platform === 'link') {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    } else {
      window.open(urls[platform as keyof typeof urls], '_blank', 'width=600,height=400');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 -mt-12 relative z-10">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 mb-8 text-lg">
        <Link
          href="/blog"
          className="text-androia-gray-500 hover:text-androia-blue transition-colors font-manrope"
        >
          Blog
        </Link>
        <span className="text-androia-gray-500">/</span>
        <span className="text-androia-text font-semibold font-manrope">{category}</span>
      </div>

      {/* Author Meta Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
        <div className="flex items-center gap-6">
          {/* Author */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Image
                src={post.author.avatar}
                alt={post.author.name}
                width={36}
                height={36}
                className="rounded-full border border-androia-gray-200"
              />
            </div>
            <span className="text-lg md:text-2xl font-semibold text-androia-text font-manrope">
              {post.author.name}
            </span>
          </div>

          {/* Likes */}
          <button
            onClick={handleLike}
            className="flex items-center gap-2 group"
            aria-label={isLiked ? 'Unlike post' : 'Like post'}
          >
            <Heart
              className={`w-6 h-6 transition-all ${
                isLiked
                  ? 'fill-red-500 stroke-red-500'
                  : 'stroke-androia-blue group-hover:fill-red-500 group-hover:stroke-red-500'
              }`}
              strokeWidth={2}
            />
            <span className="text-lg md:text-2xl font-semibold text-androia-text font-manrope">
              {likes.toLocaleString()}
            </span>
          </button>
        </div>

        {/* Date */}
        <div className="text-lg md:text-2xl font-semibold text-androia-text font-manrope">
          {formatDate(post.date)}
        </div>

        {/* Share Icons */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleShare('linkedin')}
            className="hover:scale-110 transition-transform"
            aria-label="Share on LinkedIn"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M22.2857 0H1.70893C0.766071 0 0 0.776786 0 1.73036V22.2696C0 23.2232 0.766071 24 1.70893 24H22.2857C23.2286 24 24 23.2232 24 22.2696V1.73036C24 0.776786 23.2286 0 22.2857 0ZM7.25357 20.5714H3.69643V9.11786H7.25893V20.5714H7.25357ZM5.475 7.55357C4.33393 7.55357 3.4125 6.62679 3.4125 5.49107C3.4125 4.35536 4.33393 3.42857 5.475 3.42857C6.61071 3.42857 7.5375 4.35536 7.5375 5.49107C7.5375 6.63214 6.61607 7.55357 5.475 7.55357ZM20.5875 20.5714H17.0304V15C17.0304 13.6714 17.0036 11.9625 15.1821 11.9625C13.3286 11.9625 13.0446 13.4089 13.0446 14.9036V20.5714H9.4875V9.11786H12.9V10.6821H12.9482C13.425 9.78214 14.5875 8.83393 16.3179 8.83393C19.9179 8.83393 20.5875 11.2071 20.5875 14.2929V20.5714Z"
                fill="black"
              />
            </svg>
          </button>

          <button
            onClick={() => handleShare('x')}
            className="hover:scale-110 transition-transform"
            aria-label="Share on X"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                fill="black"
              />
            </svg>
          </button>

          <button
            onClick={() => handleShare('facebook')}
            className="hover:scale-110 transition-transform"
            aria-label="Share on Facebook"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.3882 22.954 10.125 23.8542V15.4688H7.07812V12H10.125V9.35625C10.125 6.34875 11.9166 4.6875 14.6576 4.6875C15.9701 4.6875 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.34 7.875 13.875 8.80008 13.875 9.75V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z"
                fill="#1877F2"
              />
            </svg>
          </button>

          <button
            onClick={() => handleShare('link')}
            className="hover:scale-110 transition-transform"
            aria-label="Copy link"
          >
            <Link2 className="w-6 h-6 text-androia-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );
}
